import React, {
  ReactElement,
  Fragment,
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  FunnelIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import { MagnifyingGlassIcon as MagnifyingGlassIcon24Solid } from '@heroicons/react/24/solid';
import { Disclosure } from '@headlessui/react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';

import {
  AccountOption,
  Button,
  EmptyState,
  Select,
  Skeleton,
  Spinner,
} from 'src/ui/components';
import { ActivityLogFilter, ActivityLogItem } from './components';
import { ActivityAllLogItem } from './components/ActivityAllLogItem';

import { useAuth, useTitle } from 'src/hooks';
import {
  useGetActivityLog,
  useDownloadCsv,
  useGetPersonLogs,
  useGetPersonAlertLog,
  useGetHospitalForm,
  useGetAllLogs,
} from './api';

import { useActivityLogFilterReducer } from './utils/useFilterReducer';
import {
  localTzMsg,
  adminNoteMsg,
  pageLength,
  downloadCsvMsg,
} from 'src/utils/appConstants';
import { showSuccessToast } from 'src/utils/ToastNotification';
import { classNames } from 'src/utils/className';
import { activityTypeOptions } from './utils/tabsList';
import { activities, activityTypes, tabActivityOptions } from './utils/activityFilterConstants';
import { sanitizeFilterOptions } from './utils/filterSanitizer';

import { ActivityLogListType } from './types/activityLogType';
import { ActivityLogFilterType } from './types/filterOptionsType';

export default function ActivityLog(): ReactElement {
  useTitle('View Activity Log');

  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  const { isExternalUser } = useAuth();

  const [selectedToggleValue, setSelectedToggleValue] = useState<string>(() => {
    if (isExternalUser) return '';
    return (
      window.localStorage.getItem('ActivityLogSelectedTab') ?? 'callDocuments'
    );
  });
  /*has the selcted accounts from accounts dropdown filter */
  const [selected, setSelected] = useState<AccountOption[]>([]);

  useEffect(() => {
    const key = `ActivityLogFilter_${selectedToggleValue}`;
    const saved = window.localStorage.getItem(key);
    if (saved) {
      try {
        const parsed: ActivityLogFilterType = JSON.parse(saved);
        if (parsed.accountCidPid) {
          const ids = JSON.parse(parsed.accountCidPid);
          setSelected(
            ids.map((id: number) => ({ value: id, label: String(id) })),
          );
        } else {
          setSelected([]);
        }
      } catch {
        setSelected([]);
      }
    } else {
      setSelected([]);
    }
  }, [selectedToggleValue]);

  useEffect(() => {
    const key = `ActivityLogSelectedTab`;
    window.localStorage.setItem(key, selectedToggleValue);
  }, [selected, selectedToggleValue]);

  const {
    filterOptions,
    filterCount,
    resetFilters,
    setValues,
    setCategory,
    setCategoryActivity,
    setTabActivity,
    setSource,
  } = useActivityLogFilterReducer(selectedToggleValue, isExternalUser);

  const safeFilterOptions = useMemo(
    () =>
      sanitizeFilterOptions(filterOptions, selectedToggleValue, isExternalUser),
    [filterOptions, isExternalUser, selectedToggleValue],
  );

  const isActivityEmpty = useMemo(() => {
    if (selectedToggleValue === 'all') {
      const typeIsEmpty =
        Array.isArray(filterOptions.type) && filterOptions.type.length === 0;
      const categoryActivityIsEmpty =
        Array.isArray(filterOptions.categoryActivity) &&
        filterOptions.categoryActivity.length === 0;
      return typeIsEmpty || categoryActivityIsEmpty;
    }
    if (tabActivityOptions[selectedToggleValue]) {
      return (
        Array.isArray(filterOptions.tabActivity) &&
        filterOptions.tabActivity.length === 0
      );
    }
    return false;
  }, [
    filterOptions.type,
    filterOptions.categoryActivity,
    filterOptions.tabActivity,
    selectedToggleValue,
  ]);

  const isAnyFilterApplied = useMemo(() => {
    if (!safeFilterOptions) return false;

    return Object.entries(safeFilterOptions).some(([key, value]) => {
      if (key === 'sortType' || key === 'sortOrder') return false;

      if (Array.isArray(value)) return value.length > 0;

      return value !== undefined && value !== null && value !== '';
    });
  }, [safeFilterOptions]);

  const {
    data: callDocuments,
    isLoading: isCallDocLoading,
    isFetchingNextPage: isFetchingCallDocNextPage,
    hasNextPage: hasNextCallDocPage,
    fetchNextPage: fetchNextCallDocPage,
  } = useGetActivityLog(safeFilterOptions, selectedToggleValue, isExternalUser, isActivityEmpty);

  const {
    data: personLog,
    isLoading: isRepoLogLoading,
    isFetching,
    isRefetching,
    isFetchingNextPage: isFetchingPersonLogNextPage,
    hasNextPage: hasNextPersonLogPage,
    fetchNextPage: fetchNextPersonLogPage,
  } = useGetPersonLogs(safeFilterOptions, selectedToggleValue, isActivityEmpty);

  const {
    data: personAlertLog,
    isLoading: isAlertLogLoading,
    hasNextPage: hasNextPersonAlertPage,
    isFetchingNextPage: isFetchingPersonAlertNextPage,
    fetchNextPage: fetchNextPersonAlertPage,
  } = useGetPersonAlertLog(safeFilterOptions, selectedToggleValue, isActivityEmpty);

  const {
    data: hospitalLog,
    isLoading: isHFLogLoading,
    hasNextPage: hasNextHospitalPage,
    isFetchingNextPage: isFetchingHFNextPage,
    fetchNextPage: fetchNextHospitalPage,
  } = useGetHospitalForm(safeFilterOptions, selectedToggleValue, isActivityEmpty);

  const {
    data: allLogs,
    isLoading: isAllLogLoading,
    hasNextPage: hasNextAllPage,
    isFetchingNextPage: isFetchingAllNextPage,
    fetchNextPage: fetchNextAllPage,
  } = useGetAllLogs(safeFilterOptions, selectedToggleValue, isAnyFilterApplied, isActivityEmpty);

  useEffect(() => {
    if (activityTypes && !filterOptions.type) {
      setCategory([...activityTypes]);
    }
  }, [setCategory, filterOptions.type]);

  useEffect(() => {
    if (activities && !filterOptions.categoryActivity) {
      setCategoryActivity([...activityTypes]);
    }
  }, [setCategoryActivity, filterOptions.categoryActivity]);

  const requestType = useMemo(() => {
    if (selectedToggleValue === 'personRepository') {
      return 'person';
    }
    if (selectedToggleValue === 'personAlerts') {
      return 'personalert';
    }
    if (selectedToggleValue === 'hospitalForm') {
      return 'activitytracker';
    }
    if (selectedToggleValue === 'callDocuments' || selectedToggleValue === '') {
      return 'calldocument';
    }
    if (selectedToggleValue === 'all') {
      return 'all';
    }
    return '';
  }, [selectedToggleValue]);

  const { mutate: onDownload, isLoading: isDownloading } = useDownloadCsv(
    requestType,
    isExternalUser,
  );

  const emptyPages = useMemo(
    () => ({
      pages: [
        {
          data: [],
          draw: 1,
          error: null,
          isAutoRefreshEnabled: false,
          pageNumber: 0,
          recordsFiltered: 0,
          recordsTotal: 0,
        },
      ],
    }),
    [],
  );

  const data = useMemo(() => {
    if (isActivityEmpty) return emptyPages;
    if (selectedToggleValue === 'personRepository') {
      return personLog;
    }
    if (selectedToggleValue === 'personAlerts') {
      return personAlertLog;
    }
    if (selectedToggleValue === 'hospitalForm') {
      return hospitalLog;
    }
    if (selectedToggleValue === 'callDocuments' || selectedToggleValue === '') {
      return callDocuments;
    }
    if (selectedToggleValue === 'all') {
      return allLogs;
    }
    return emptyPages;
  }, [
    isActivityEmpty,
    emptyPages,
    allLogs,
    callDocuments,
    hospitalLog,
    personAlertLog,
    personLog,
    selectedToggleValue,
  ]);

  /*for showing the options in the sort drop down menu based on the selected Toggle value */
  const returnSortOptions = useCallback(
    (selectedToggleValue: string) => {
      if (isExternalUser) {
        return [
          { id: 4, label: 'Activity date', value: 'CreatedOn' },
          { id: 2, label: 'CID', value: 'CID' },
          { id: 3, label: 'PID', value: 'PID' },
          { id: 6, label: 'File name', value: 'ReportFileName' },
          { id: 1, label: 'Activity', value: 'Activity' },
          { id: 5, label: 'User', value: 'UserName' },
          { id: 7, label: 'Source', value: 'Source' },
        ];
      }
      const baseSortOptions = [
        { id: 4, label: 'Activity date', value: 'CreatedOn' },
        { id: 1, label: 'Account CID PID', value: 'AccountCIDPID' },
        { id: 3, label: 'Activity', value: 'Activity' },
        { id: 5, label: 'User', value: 'UserName' },
      ];

      const allTabOptions = [
        { id: 4, label: 'Activity date', value: 'CreatedOn' },
      ];

      const tabSpecificOptions: Record<
        string,
        Array<{ id: number; label: string; value: string }>
      > = {
        personAlerts: [
          { id: 8, label: 'Alert Number', value: 'PersonAlertId' },
        ],
        personRepository: [{ id: 7, label: 'Person ID', value: 'PersonID' }],
        hospitalForm: [
          { id: 9, label: 'Submission Number', value: 'SubmissionNumber' },
        ],
        callDocuments: [{ id: 6, label: 'File name', value: 'ReportFileName' }],
        all: [
          { id: 10, label: 'Type', value: 'Category' },
          { id: 11, label: 'ID', value: 'Id' },
        ],
      };

      const injected = tabSpecificOptions[selectedToggleValue] ?? [];

      if (selectedToggleValue === 'all') {
        // const beforeActivity = baseSortOptions.findIndex(
        //   opt => opt.value === 'Activity',
        // );
        // const afterActivity = beforeActivity + 1;

        // return [
        //   ...baseSortOptions.slice(0, beforeActivity),
        //   injected[0],
        //   baseSortOptions[beforeActivity],
        //   injected[1],
        //   ...baseSortOptions.slice(afterActivity),
        // ];
        return [...allTabOptions];
      }

      const insertIndex = baseSortOptions.findIndex(
        opt => opt.value === 'Activity',
      );
      return [
        ...baseSortOptions.slice(0, insertIndex),
        ...injected,
        ...baseSortOptions.slice(insertIndex),
      ];
    },
    [isExternalUser],
  );

  const sortOptions = useMemo(
    () => returnSortOptions(selectedToggleValue),
    [selectedToggleValue, returnSortOptions],
  );

  // keep previous tab and saved selection for callDocuments only
  // const prevToggleRef = useRef<string>(selectedToggleValue);

  // const filterStateRef = useRef<Record<string, ActivityLogFilterType>>({});

  // useEffect(() => {
  //   const prev = prevToggleRef.current;
  //   const current = selectedToggleValue;

  //   if (prev === current) return;

  //   // save previous tab filters
  //   filterStateRef.current[prev] = filterOptions;

  //   // restore filters for new tab
  //   const savedFilters = filterStateRef.current[current];

  //   if (savedFilters) {
  //     Object.entries(savedFilters).forEach(([key, value]) => {
  //       if (typeof value === 'string') {
  //         setValues(key as keyof ActivityLogFilterType, value);
  //       }
  //     });
  //   } else {
  //     resetFilters();
  //   }

  //   prevToggleRef.current = current;
  // }, [filterOptions, resetFilters, selectedToggleValue, setValues]);

  const isLoading =
    !isActivityEmpty &&
    (isAlertLogLoading || isRepoLogLoading || isHFLogLoading || isCallDocLoading);

  const isNonBlockingLoading =
    isFetchingCallDocNextPage ||
    isRefetching ||
    isDownloading ||
    isFetchingHFNextPage ||
    isFetchingPersonAlertNextPage ||
    isFetchingPersonLogNextPage ||
    isFetchingAllNextPage;

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const isBottom =
          e.target.scrollHeight - e.target.scrollTop <=
          e.target.clientHeight + 10;

        if (isBottom) {
          if (hasNextCallDocPage) fetchNextCallDocPage();
          if (hasNextPersonLogPage) fetchNextPersonLogPage();
          if (hasNextPersonAlertPage) fetchNextPersonAlertPage();
          if (hasNextHospitalPage) fetchNextHospitalPage();
          if (hasNextAllPage) fetchNextAllPage();
        }
      }
    },
    [
      hasNextCallDocPage,
      fetchNextCallDocPage,
      hasNextPersonLogPage,
      fetchNextPersonLogPage,
      hasNextPersonAlertPage,
      fetchNextPersonAlertPage,
      hasNextHospitalPage,
      fetchNextHospitalPage,
      hasNextAllPage,
      fetchNextAllPage,
    ],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  //download functionality
  function downloadAsCsv() {
    const params = {
      filterOptions: safeFilterOptions,
      pageParam: pageLength,
    };
    onDownload(params, {
      onSuccess: () => {
        showSuccessToast({
          message: downloadCsvMsg,
        });
      },
    });
  }

  const toggleGroupItemClasses = classNames(
    'relative inline-flex items-center px-4 py-1.5 text-xs font-semibold focus:z-20',
    'data-[state=on]:z-10 data-[state=on]:bg-primary data-[state=on]:text-white data-[state=on]:outline-offset-2 data-[state=on]:focus-visible:outline-2 data-[state=on]:focus-visible:outline-offset-2 data-[state=on]:focus-visible:outline-primary-600',
    'data-[state=off]:bg-gray-50 data-[state=off]:text-light-darkest data-[state=off]:ring-1 data-[state=off]:ring-inset data-[state=off]:ring-light-light data-[state=off]:hover:bg-gray-100 data-[state=off]:focus:outline-offset-0 dark:data-[state=off]:text-light-light dark:data-[state=off]:ring-dark-medium dark:data-[state=off]:hover:bg-neutral-700 dark:data-[state=off]:bg-neutral-800',
  );

  function renderHeader() {
    return (
      <Disclosure>
        {({ open }) => (
          <>
            <div className="flex flex-col gap-4 border-t border-gray-200 bg-white px-2 py-1 shadow dark:border-neutral-700/50 dark:bg-neutral-800 sm:px-3 md:flex-row md:items-center md:gap-8 lg:px-4">
              <h2 className="text-md font-semibold text-gray-950 dark:text-white sm:text-xl sm:tracking-tight">
                Activity log
              </h2>
              <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-8">
                <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-8">
                  <div className="mt-1 flex items-center gap-4 sm:mt-0">
                    <div className="relative inline-flex">
                      <Disclosure.Button
                        as={Button}
                        size="small"
                        variant={open ? 'primary' : 'subtle'}
                      >
                        <FunnelIcon
                          className="-ml-0.5 h-5 w-5"
                          aria-hidden="true"
                        />
                        <span className="hidden sm:inline-block">Filter</span>
                      </Disclosure.Button>
                      <span className="absolute right-0 top-0 -mr-1.5 -mt-1.5 flex h-5 w-5">
                        {filterCount > 0 ? (
                          <span
                            className={`relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold leading-none text-white`}
                          >
                            {filterCount}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <Button
                      variant="subtle"
                      size="small"
                      onClick={() => {
                        setSelected([]);
                        resetFilters();
                      }}
                    >
                      <ArrowPathIcon
                        className="-ml-0.5 h-5 w-5"
                        aria-hidden="true"
                      />
                      <span className="hidden sm:block">Reset</span>
                    </Button>
                  </div>
                  {!isExternalUser && (
                    <>
                      <ToggleGroup.Root
                        className="shadow-xs isolate my-1 hidden -space-x-px rounded-md lg:inline-flex"
                        type="single"
                        defaultValue="all"
                        aria-label="Text alignment"
                        value={selectedToggleValue}
                        onValueChange={(value: string) => {
                          if (value) {
                            setSelectedToggleValue(value);
                          }
                        }}
                      >
                        <ToggleGroup.Item
                          className={classNames(
                            'rounded-l-md',
                            toggleGroupItemClasses,
                          )}
                          value="callDocuments"
                        >
                          Call documents
                        </ToggleGroup.Item>
                        <ToggleGroup.Item
                          className={toggleGroupItemClasses}
                          value="personAlerts"
                        >
                          Person alerts
                        </ToggleGroup.Item>
                        <ToggleGroup.Item
                          className={toggleGroupItemClasses}
                          value="personRepository"
                        >
                          Person repository
                        </ToggleGroup.Item>
                        <ToggleGroup.Item
                          className={toggleGroupItemClasses}
                          value="hospitalForm"
                        >
                          Hospital form
                        </ToggleGroup.Item>
                        <ToggleGroup.Item
                          className={classNames(
                            'rounded-r-md',
                            toggleGroupItemClasses,
                          )}
                          value="all"
                        >
                          All
                        </ToggleGroup.Item>
                      </ToggleGroup.Root>
                      {/* Responsive */}
                      <div className="flex items-center gap-2 text-xs font-semibold lg:hidden">
                        <label
                          htmlFor="activity-type-options"
                          className="hidden md:inline-flex"
                        >
                          Activity type
                        </label>
                        <Select
                          inputSize="small"
                          id="activity-type-options"
                          options={activityTypeOptions}
                          value={selectedToggleValue}
                          onChange={(e: any) => {
                            if (e && e.target && e.target.value) {
                              setSelectedToggleValue(e.target.value);
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>

                {data && data.pages[0].data?.length ? (
                  <div className="flex gap-4">
                    <Button
                      variant="subtle"
                      size="small"
                      onClick={() => downloadAsCsv()}
                    >
                      <ArrowDownTrayIcon
                        className="-ml-0.5 h-5 w-5"
                        aria-hidden="true"
                      />
                      <span className="hidden sm:block">Download as CSV</span>
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>

            <ActivityLogFilter
              filterOptions={filterOptions}
              setValues={setValues}
              resetFilters={resetFilters}
              selectedToggleValue={selectedToggleValue}
              sortOptions={sortOptions}
              setCategoryActivity={setCategoryActivity}
              setTabActivity={setTabActivity}
              setCategory={setCategory}
              selected={selected}
              setSelected={setSelected}
              setSource={setSource}
            />
          </>
        )}
      </Disclosure>
    );
  }

  function renderInfoText() {
    return (
      <>
        <p className="flex min-w-0 flex-wrap items-center justify-center gap-x-4 gap-y-1 border-y border-sky-200/50 bg-blue-50  p-1 text-center text-xs font-medium text-sky-700  dark:border-neutral-700  dark:bg-neutral-800 dark:text-sky-300 md:justify-end ">
          <span className="inline-flex gap-1">
            <InformationCircleIcon
              className="-ml-0.5 hidden h-4 w-4 shrink-0 md:inline-block"
              aria-hidden="true"
            />
            {localTzMsg}
          </span>
          <span className="hidden divide-x self-stretch sm:inline-block">
            |
          </span>

          <span className="inline-flex gap-1">
            <InformationCircleIcon
              className="-ml-0.5 hidden h-4 w-4 shrink-0 md:inline-block"
              aria-hidden="true"
            />
            {adminNoteMsg}
          </span>
        </p>
      </>
    );
  }

  function renderPageDetails(): ReactElement {
    return (
      <>
        {data?.pages &&
          data.pages.length > 0 &&
          data?.pages[0]?.data?.length > 0 &&
          data.pages[data.pages.length - 1]?.data.length && (
            <>
              Showing <strong>1</strong> to{' '}
              <strong>
                {data?.pages[data?.pages?.length - 1]?.data?.length ===
                pageLength
                  ? data?.pages?.length * pageLength
                  : (data?.pages?.length - 1) * pageLength +
                    data?.pages[data?.pages?.length - 1]?.data.length}{' '}
              </strong>
              {/* of{' '}
              <strong>
                {data?.pages[data?.pages?.length - 1]?.recordsFiltered}
              </strong>{' '}
              entries (filtered from{' '}
              <strong> {data?.pages[0]?.recordsTotal} </strong> total entries) */}
              {isFetching ? ' | Processing' : ''}
            </>
          )}
      </>
    );
  }

  return (
    <main className="flex flex-1 flex-col overflow-y-auto overscroll-contain">
      {renderHeader()}
      {renderInfoText()}
      <div className="relative flex min-h-[40px] flex-wrap items-center justify-between gap-x-6 gap-y-3 p-2 py-1 sm:px-3 lg:px-4">
        <p className="shrink text-xs text-gray-600 dark:text-neutral-400">
          {renderPageDetails()}
        </p>
      </div>
      <div
        className="relative flex-1 space-y-2 overflow-y-scroll px-2 pb-2 sm:px-3 sm:pb-3 lg:px-4 lg:pb-4"
        ref={tbodyRef}
      >
        {selectedToggleValue !== 'all' && !isLoading && data?.pages ? (
          <>
            {data?.pages.map((page: any, index: number) => (
              <Fragment key={`${page} - ${index + 1}`}>
                {page?.data && page?.data?.length ? (
                  page?.data.map(
                    (activityLog: ActivityLogListType, indx: number) => (
                      <ActivityLogItem
                        key={`${activityLog.Id}-${indx + 1}`}
                        activityLog={activityLog}
                        selectedToggleValue={selectedToggleValue}
                      />
                    ),
                  )
                ) : (
                  <div className="absolute inset-0 grid place-content-center">
                    <EmptyState message="No information available" />
                  </div>
                )}
              </Fragment>
            ))}
          </>
        ) : selectedToggleValue === 'all' && !isAllLogLoading && data?.pages ? (
          <>
            {data?.pages.map((page: any, index: number) => (
              <Fragment key={`${page} - ${index + 1}`}>
                {page?.data && page?.data?.length ? (
                  page?.data.map(
                    (activityLog: ActivityLogListType, indx: number) => (
                      <ActivityAllLogItem
                        key={`${activityLog?.Id}-${indx + 1}`}
                        activityLog={activityLog}
                      />
                    ),
                  )
                ) : (
                  <div className="absolute inset-0 grid place-content-center">
                    <EmptyState message="No information available" />
                  </div>
                )}
              </Fragment>
            ))}
          </>
        ) : (
          (isLoading || isFetching || isAllLogLoading) && <Skeleton count={8} />
        )}
        {isNonBlockingLoading && (
          <div data-testid="spinner-wrapper" className="fixed inset-0 z-20 grid place-content-center">
            <Spinner size="large" />
          </div>
        )}

        {/* All tab initial load */}
        {selectedToggleValue === 'all' && !data?.pages && !isAllLogLoading && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-500">
            <MagnifyingGlassIcon24Solid className="size-14" />
            <span className="">
              Use the filters to find the results based on the selected criteria
            </span>
          </div>
        )}
      </div>
    </main>
  );
}
